import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { FileText, FileSpreadsheet } from 'lucide-react';
import { getAttendanceCards } from '../../../services/timesheetService';
import { exportReport, exportParams } from '../../../services/reportExportService';
import formatMinutes from '../../../utils/formatMinutes';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import { translateTimesheetStatuses } from '../../../utils/timesheetStatusLabels';
import { buildReportLabels, statusReportLabels } from '../../../utils/reportLabels';
import IconButton from '../../../components/ui/IconButton';
import Tooltip from '../../../components/ui/Tooltip';
import { useTaFilterColumns } from './taFilterColumns';
import { useTaListFetch } from './useTaListFetch';

export const ATTENDANCE_CARD_EXPORT_URL = '/v1/ta/reports/attendance-cards/export';

const RowActions = styled.div`
  display: inline-flex;
  gap: 6px;
`;

// Ondalık gün (yarım gün izin 0,5): dilin ondalık ayırıcısıyla, en çok iki basamak.
export const formatDays = (value, lang) => {
  const n = Number(value);
  if (value === null || value === undefined || Number.isNaN(n)) return '-';
  return n.toLocaleString(lang || 'tr', { maximumFractionDigits: 2 });
};

/**
 * Devam kartı dosyasının etiketleri (web dili): kartın gün tablosu sütunları, kart başlığı alanları, imza alanları, gün
 * durumları. Sunucu (AttendanceCardBuilder) aynı anahtarları okur; gönderilmeyen anahtar Türkçe varsayılana düşer. Puantajım
 * (kişinin kendi kartı) da aynı etiketleri gönderir.
 */
export function attendanceCardLabels(t) {
  return {
    title: t('taReports.attendanceCard.title'),
    date: t('timesheet.date'),
    weekday: t('taReports.attendanceCard.weekday'),
    plannedShift: t('taReports.columns.plannedShift'),
    sessions: t('taReports.attendanceCard.sessions'),
    actualMinutes: t('timesheet.actual'),
    lateMinutes: t('timesheet.late'),
    earlyLeaveMinutes: t('taReports.columns.earlyLeave'),
    undertimeMinutes: t('taReports.undertime.column'),
    totalComputedOvertimeMinutes: t('taReports.columns.computedOvertime'),
    approvedOvertimeMinutes: t('taReports.columns.approvedOvertime'),
    leave: t('taReports.attendanceCard.leave'),
    status: t('timesheet.status'),
    registrationNumber: t('taReports.attendanceCard.registrationNumber'),
    workedDays: t('taReports.attendanceCard.workedDays'),
    leaveDays: t('taReports.attendanceCard.leaveDays'),
    medicalLeaveDays: t('taReports.attendanceCard.medicalLeaveDays'),
    hourlyLeaveMinutes: t('payroll.hourlyLeaveMinutes'),
    absentDays: t('taReports.attendanceCard.absentDays'),
    'signature.employee': t('taReports.attendanceCard.signatureEmployee'),
    'signature.manager': t('taReports.attendanceCard.signatureManager'),
    ...statusReportLabels(translateTimesheetStatuses(t)),
  };
}

/**
 * AKM POC §7.2 — Personel Devam Kartı kişi listesi (dönem toplamları). Dönem süzgeci görünmez bir tarih sütunudur
 * (boşsa sunucu bu ayı alır, en çok 31 gün); satırda kişinin kartı PDF / Excel (aynı uç, personId = satırın kişisi).
 */
export function useAttendanceCardReport() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const list = useTaListFetch(getAttendanceCards);
  const filterColumns = useTaFilterColumns(t);
  const [running, setRunning] = useState(null);

  const exportLabels = useCallback(() => attendanceCardLabels(t), [t]);

  // Satır eylemi: o kişinin kartı — listenin dönemi ve süzgeçleriyle, kişi satırın kişisi.
  const exportRow = useCallback(async (row, format) => {
    if (!row?.userId || running) return;
    setRunning(`${row.userId}:${format}`);
    try {
      await exportReport(ATTENDANCE_CARD_EXPORT_URL, {
        format,
        params: { ...exportParams(list.queryParams), personId: row.userId },
        labels: buildReportLabels(t, [], t('taReports.attendanceCard.title'), attendanceCardLabels(t)),
      });
    } finally {
      setRunning(null);
    }
  }, [list.queryParams, running, t]);

  const { data } = list;
  const columns = useMemo(() => {
    const period = dateFilterColumn('date', t('taReports.filters.period'), lang);
    return [
      { name: 'userId', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
      {
        name: 'fullName', label: t('timesheet.employee'),
        options: {
          filter: true, sort: false, filterType: 'textField',
          filterOptions: { filterKey: 'userFullName[like]' },
        },
      },
      {
        name: 'registrationNumber', label: t('taReports.attendanceCard.registrationNumber'),
        options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
      },
      {
        name: 'departmentName', label: t('taReports.attendanceCard.department'),
        options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
      },
      { name: 'workedDays', label: t('taReports.attendanceCard.workedDays'), options: { filter: false, sort: false } },
      {
        name: 'workedMinutes', label: t('taReports.attendanceCard.workedMinutes'),
        options: { filter: false, sort: false, customBodyRender: (v) => formatMinutes(v) },
      },
      {
        name: 'lateMinutes', label: t('timesheet.late'),
        options: { filter: false, sort: false, customBodyRender: (v) => formatMinutes(v) },
      },
      {
        name: 'earlyLeaveMinutes', label: t('taReports.columns.earlyLeave'),
        options: { filter: false, sort: false, customBodyRender: (v) => formatMinutes(v) },
      },
      {
        name: 'undertimeMinutes', label: t('taReports.undertime.column'),
        options: { filter: false, sort: false, customBodyRender: (v) => formatMinutes(v) },
      },
      {
        name: 'overtimeMinutes', label: t('taReports.columns.computedOvertime'),
        options: { filter: false, sort: false, customBodyRender: (v) => formatMinutes(v) },
      },
      {
        name: 'approvedOvertimeMinutes', label: t('taReports.columns.approvedOvertime'),
        options: { filter: false, sort: false, customBodyRender: (v) => formatMinutes(v) },
      },
      {
        // İnceleme I1/M1 — bordro özetiyle aynı hesap: İzinli raporlu HARİÇ, Raporlu ayrı, saatlik izin süre olarak.
        name: 'leaveDays', label: t('taReports.attendanceCard.leaveDays'),
        options: { filter: false, sort: false, customBodyRender: (v) => formatDays(v, lang) },
      },
      {
        name: 'medicalLeaveDays', label: t('taReports.attendanceCard.medicalLeaveDays'),
        options: { filter: false, sort: false, customBodyRender: (v) => formatDays(v, lang) },
      },
      {
        name: 'hourlyLeaveMinutes', label: t('payroll.hourlyLeaveMinutes'),
        options: { filter: false, sort: false, customBodyRender: (v) => formatMinutes(v) },
      },
      { name: 'absentDays', label: t('taReports.attendanceCard.absentDays'), options: { filter: false, sort: false } },
      {
        name: 'cardActions', label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false, viewColumns: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            if (!row) return null;
            const busy = running !== null;
            return (
              <RowActions>
                <Tooltip text={t('taReports.attendanceCard.downloadPdf')}>
                  <IconButton
                    type="button"
                    size="sm"
                    aria-label={t('taReports.attendanceCard.downloadPdf')}
                    data-action="card-pdf"
                    disabled={busy}
                    onClick={() => exportRow(row, 'pdf')}
                  >
                    <FileText size={14} aria-hidden="true" />
                  </IconButton>
                </Tooltip>
                <Tooltip text={t('taReports.attendanceCard.downloadExcel')}>
                  <IconButton
                    type="button"
                    size="sm"
                    aria-label={t('taReports.attendanceCard.downloadExcel')}
                    data-action="card-xlsx"
                    disabled={busy}
                    onClick={() => exportRow(row, 'xlsx')}
                  >
                    <FileSpreadsheet size={14} aria-hidden="true" />
                  </IconButton>
                </Tooltip>
              </RowActions>
            );
          },
        },
      },
      { ...period, options: { ...period.options, display: 'excluded', viewColumns: false, download: false, sort: false } },
      ...filterColumns,
    ];
  }, [t, lang, data, filterColumns, running, exportRow]);

  return { ...list, columns, exportLabels };
}
